import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/Card';

interface QuickAction {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  color: string;
}

interface QuickActionsProps {
  className?: string;
}

const quickActions: QuickAction[] = [
  {
    id: 'add-lead',
    name: 'Add Lead',
    description: 'Create a new lead',
    path: '/leads/add',
    color: 'bg-blue-500 hover:bg-blue-600',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
        />
      </svg>
    ),
  },
  {
    id: 'schedule-visit',
    name: 'Schedule Visit',
    description: 'Book a site visit',
    path: '/visits/schedule',
    color: 'bg-indigo-500 hover:bg-indigo-600',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
    ),
  },
  {
    id: 'request-topup',
    name: 'Request Topup',
    description: 'Add wallet credits',
    path: '/wallet/topup',
    color: 'bg-green-500 hover:bg-green-600',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
  {
    id: 'send-referral',
    name: 'Send Referral',
    description: 'Refer a lead to broker',
    path: '/referrals/send',
    color: 'bg-amber-500 hover:bg-amber-600',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M17 8l4 4m0 0l-4 4m4-4H3"
        />
      </svg>
    ),
  },
];

export const QuickActions: React.FC<QuickActionsProps> = ({ className = '' }) => {
  const navigate = useNavigate();

  return (
    <Card className={`h-full ${className}`} padding="none">
      {/* Header */}
      <div className="px-6 py-4 border-b border-secondary-200">
        <h3 className="text-lg font-semibold text-secondary-900">Quick Actions</h3>
      </div>

      {/* Actions Grid */}
      <div className="p-4">
        <div className="grid grid-cols-2 gap-3">
          {quickActions.map((action) => (
            <button
              key={action.id}
              onClick={() => navigate(action.path)}
              className="group flex flex-col items-center p-4 rounded-xl border border-secondary-200 hover:border-transparent hover:shadow-md transition-all duration-200 bg-white"
            >
              <div
                className={`p-3 rounded-xl ${action.color} text-white mb-3 transition-transform group-hover:scale-110`}
              >
                {action.icon}
              </div>
              <span className="text-sm font-medium text-secondary-900 text-center">
                {action.name}
              </span>
              <span className="text-xs text-secondary-500 mt-1 text-center">
                {action.description}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Additional Quick Links */}
      <div className="px-4 pb-4">
        <div className="border-t border-secondary-100 pt-4">
          <p className="text-xs text-secondary-400 uppercase tracking-wider mb-3">
            Quick Links
          </p>
          <div className="space-y-2">
            <button
              onClick={() => navigate('/leads')}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-secondary-600 rounded-lg hover:bg-secondary-50 transition-colors"
            >
              <svg className="w-4 h-4 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              View All Leads
            </button>
            <button
              onClick={() => navigate('/visits')}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-secondary-600 rounded-lg hover:bg-secondary-50 transition-colors"
            >
              <svg className="w-4 h-4 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Visit History
            </button>
            <button
              onClick={() => navigate('/billing')}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-secondary-600 rounded-lg hover:bg-secondary-50 transition-colors"
            >
              <svg className="w-4 h-4 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Invoices
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default QuickActions;
